/**
 * Utilities for formatting PromQL metrics and label names according to Prometheus 3.0 spec.
 */

const legacyCharactersForLabels = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
const legacyCharactersForMetrics = /^[a-zA-Z_:][a-zA-Z0-9_:]*$/; // Legacy metrics can include colons, legacy labels cannot.

/**
 * Check if a label includes the UTF-8 characters introduced by Prometheus 3.0.
 * These characters include dots, dashes, and slashes, e.g. `my.label`, `my-label`, `my/label`.
 * Labels with UTF-8 characters must be quoted.
 *
 * https://prometheus.io/docs/guides/utf8
 */
export function labelNameHasUTF8Characters(labelName: string): boolean {
  if (!labelName) return false;
  return !legacyCharactersForLabels.test(labelName);
}

/**
 * Check if a metric includes the UTF-8 characters introduced by Prometheus 3.0.
 * These characters include dots, dashes, and slashes, e.g. `my.metric`, `my-metric`, `my/metric`.
 * Metrics with UTF-8 characters must be quoted.
 *
 * https://prometheus.io/docs/guides/utf8
 */
export function metricHasUTF8Characters(metric: string): boolean {
  if (!metric) return false;
  return !legacyCharactersForMetrics.test(metric);
}

/**
 * Escape special characters in a string for use in PromQL within a string literal enclosed in
 * double quotes, e.g. foo"bar -> foo\\"bar.
 * Escapes backslashes and double quotes.
 */
export function escapeString(str: string): string {
  return str.replace(/([\\"])/g, '\\$1');
}

/*
 * Wrap a string in quotes, and escape any characters within the string if necessary.
 * Unlike escapeString, this will produce a valid StringLiteral.
 */
export function quoteString(text: string): string {
  return `"${escapeString(text)}"`;
}

/**
 * Quote a label name if it contains characters that require quoting.
 * Returns the label name with quotes and proper escaping if needed, otherwise returns it as-is.
 */
export function maybeQuoteLabelName(labelName: string): string {
  if (labelNameHasUTF8Characters(labelName)) {
    return quoteString(labelName);
  }
  return labelName;
}

/**
 * Convert a metric name to a valid PromQL expression.
 * If the metric name contains special characters (dots, dashes, UTF-8, etc.),
 * it will be wrapped in `{__name__=`...`}` syntax.
 * Standard metric names are returned as-is.
 */
export function metricNameToPromQLExpr(metricName: string): string {
  if (!metricHasUTF8Characters(metricName)) {
    return metricName;
  }
  return `{__name__=\`${metricName}\`}`;
}
